import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTicketDraft } from '../tickets/useTicketDraft';
import {
  ANNOUNCEMENT_KIND,
  ANNOUNCEMENT_KIND_LABEL,
  type Announcement,
  type AnnouncementKind,
} from '@electrodavisan/shared';
import { ApiError } from '../../services/api';
import { Button, Field, Input, Modal, Select, Textarea, useToast } from '../../components/ui';
import {
  useCreateAnnouncement,
  useUpdateAnnouncement,
  type AnnouncementInput,
} from '../../services/announcements.service';

const OPCIONES_CLASE = Object.values(ANNOUNCEMENT_KIND).map((valor) => ({
  value: valor,
  label: ANNOUNCEMENT_KIND_LABEL[valor],
}));

const VACIO: AnnouncementInput = {
  title: '',
  body: '',
  kind: ANNOUNCEMENT_KIND.INFO,
  publish: false,
  pinned: false,
};

/**
 * Escribir o editar un anuncio.
 *
 * ## Publicar es un boton aparte
 *
 * Hay dos botones: «Guardar borrador» y «Publicar». No un interruptor dentro
 * del formulario, porque un interruptor deja la decision en el mismo gesto que
 * guardar y asi es como sale a toda la empresa un texto a medio escribir.
 *
 * Al editar algo YA publicado desaparece esa distincion: sigue publicado y el
 * boton solo dice «Guardar cambios». Retirarlo es otra accion distinta.
 */
type AnnouncementFormProps = Parameters<typeof AnnouncementForm>[0];
export function AnnouncementFormModal(props: Omit<AnnouncementFormProps, 'draftKey'>): JSX.Element {
  const auth = useAuth();
  const profile = auth?.user?.profile;
  const draftKey = profile?.sub
    ? `announcement-draft:v1:${encodeURIComponent(profile.iss ?? '')}:${encodeURIComponent(profile.sub)}:${props.announcement?.id ?? 'new'}`
    : '';
  return props.open ? (
    <AnnouncementForm
      key={draftKey || props.announcement?.id || 'new'}
      {...props}
      draftKey={draftKey}
    />
  ) : (
    <></>
  );
}

function AnnouncementForm({
  open,
  announcement,
  onClose,
  draftKey,
}: {
  draftKey: string;
  open: boolean;
  /** `null` para uno nuevo. */
  announcement: Announcement | null;
  onClose: () => void;
}): JSX.Element {
  const draft = useTicketDraft(draftKey, { payload: '' });
  const initialized = useRef<string | null>(null);
  const [valores, setValores] = useState<AnnouncementInput>(VACIO);
  const [base, setBase] = useState<Announcement | null>(announcement);
  const [conflictVersion, setConflictVersion] = useState<Announcement | null>(null);
  const crear = useCreateAnnouncement();
  const actualizar = useUpdateAnnouncement();
  const toast = useToast();

  const editando = announcement !== null;
  const yaPublicado = base?.publishedAt != null;

  /*
   * El formulario se rellena al ABRIRSE, no en cada render.
   *
   * Sin la condicion de `open`, cualquier refresco de la lista de fondo
   * volveria a meter los valores originales encima de lo que se esta
   * escribiendo.
   */
  useEffect(() => {
    if (!open) {
      initialized.current = null;
      return;
    }
    const key = announcement?.id ?? 'new';
    if (initialized.current === key) return;
    initialized.current = key;
    setBase(announcement);
    setConflictVersion(null);
    setValores(
      announcement
        ? {
            title: announcement.title,
            body: announcement.body,
            kind: announcement.kind,
            publish: announcement.publishedAt !== null,
            pinned: announcement.pinned,
          }
        : VACIO,
    );
  }, [open, announcement]);

  const persist = (next: AnnouncementInput, original = base) => {
    if (draftKey) draft.setValues({ payload: JSON.stringify({ values: next, base: original }) });
  };
  const set = <K extends keyof AnnouncementInput>(campo: K, valor: AnnouncementInput[K]) => {
    const next = { ...valores, [campo]: valor };
    setValores(next);
    persist(next);
  };
  const recover = () => {
    try {
      const saved = JSON.parse(draft.recoverable?.payload ?? '');
      if (
        !saved.values ||
        typeof saved.values.title !== 'string' ||
        typeof saved.values.body !== 'string' ||
        !Object.values(ANNOUNCEMENT_KIND).includes(saved.values.kind) ||
        typeof saved.values.pinned !== 'boolean' ||
        (announcement &&
          (saved.base?.id !== announcement.id || !Number.isInteger(saved.base?.revision)))
      )
        throw new Error();
      setValores(saved.values);
      setBase(saved.base);
      draft.recover();
    } catch {
      toast.error('El borrador no es válido', 'Puedes descartarlo para empezar de nuevo.');
    }
  };

  const guardando = crear.isPending || actualizar.isPending;
  const incompleto = valores.title.trim().length < 3 || valores.body.trim().length < 3;

  async function guardar(publicar: boolean, republish = false): Promise<void> {
    const submittedDraft = { payload: JSON.stringify({ values: valores, base }) };
    try {
      if (editando && base) {
        const patch: Partial<AnnouncementInput> = {};
        if (valores.title.trim() !== base.title) patch.title = valores.title.trim();
        if (valores.body.trim() !== base.body) patch.body = valores.body.trim();
        if (valores.kind !== base.kind) patch.kind = valores.kind;
        if (valores.pinned !== base.pinned) patch.pinned = valores.pinned;
        if (publicar !== Boolean(base.publishedAt)) patch.publish = publicar;
        await actualizar.mutateAsync({
          id: base.id,
          expectedRevision: base.revision,
          ...patch,
          ...(republish ? { republish: true } : {}),
        });
        toast.success('Anuncio actualizado');
      } else {
        await crear.mutateAsync({ ...valores, publish: publicar });
        toast.success(
          publicar ? 'Anuncio publicado' : 'Borrador guardado',
          publicar ? 'Ya lo ve todo el ecosistema.' : 'Todavia no lo ve nadie mas.',
        );
      }
      draft.confirm(submittedDraft);
      onClose();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const current = (error.details as { current?: Announcement } | undefined)?.current;
        if (current) setConflictVersion(current);
      }
      toast.error('No se pudo guardar', error instanceof Error ? error.message : undefined);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editando ? 'Editar anuncio' : 'Nuevo anuncio'}
      description="Lo vera todo el mundo que entre al portal, sin importar su rol."
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>

          {yaPublicado && base?.firstPublishedAt && (
            <Button
              variant="secondary"
              disabled={guardando || incompleto || Boolean(conflictVersion)}
              onClick={() => {
                if (
                  window.confirm(
                    'Se enviará un nuevo aviso a todo el ecosistema. ¿Volver a anunciar?',
                  )
                )
                  void guardar(true, true);
              }}
            >
              Volver a anunciar
            </Button>
          )}
          {yaPublicado ? (
            <Button
              onClick={() => void guardar(true)}
              disabled={guardando || incompleto || Boolean(conflictVersion)}
            >
              Guardar cambios
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => void guardar(false)}
                disabled={guardando || incompleto || Boolean(conflictVersion)}
              >
                Guardar borrador
              </Button>
              <Button
                onClick={() => void guardar(true, Boolean(base?.firstPublishedAt))}
                disabled={guardando || incompleto || Boolean(conflictVersion)}
              >
                {base?.firstPublishedAt ? 'Republicar y avisar' : 'Publicar'}
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {draft.recoverable && (
          <section role="status">
            <p>Hay un anuncio guardado en este dispositivo.</p>
            <Button onClick={recover}>Recuperar anuncio</Button>
            <Button onClick={draft.discard}>Descartar borrador</Button>
          </section>
        )}
        {draft.failed && draftKey && (
          <p role="alert">
            No se pudo conservar el borrador local. No cierres esta página antes de guardarlo.
          </p>
        )}
        {conflictVersion && (
          <section role="alert" className="space-y-2 rounded border p-3">
            <p>
              Otra persona guardó cambios. Tu texto se conserva abajo. Esta es la versión actual:
            </p>
            <strong>{conflictVersion.title}</strong>
            <p className="whitespace-pre-wrap">{conflictVersion.body}</p>
            <p>
              Clase: {ANNOUNCEMENT_KIND_LABEL[conflictVersion.kind]}. Fijado:{' '}
              {conflictVersion.pinned ? 'Sí' : 'No'}. Estado:{' '}
              {conflictVersion.publishedAt ? 'Publicado' : 'Borrador'}.
            </p>
            <Button
              variant="secondary"
              onClick={() => {
                // Preserve local edits; adopt only fields the user did not change.
                setValores((v) => ({
                  ...v,
                  title: v.title === base?.title ? conflictVersion.title : v.title,
                  body: v.body === base?.body ? conflictVersion.body : v.body,
                  kind: v.kind === base?.kind ? conflictVersion.kind : v.kind,
                  pinned: v.pinned === base?.pinned ? conflictVersion.pinned : v.pinned,
                }));
                persist(
                  {
                    ...valores,
                    title: valores.title === base?.title ? conflictVersion.title : valores.title,
                    body: valores.body === base?.body ? conflictVersion.body : valores.body,
                    kind: valores.kind === base?.kind ? conflictVersion.kind : valores.kind,
                    pinned:
                      valores.pinned === base?.pinned ? conflictVersion.pinned : valores.pinned,
                  },
                  conflictVersion,
                );
                setBase(conflictVersion);
                setConflictVersion(null);
              }}
            >
              Revisé la versión actual; conservar mis cambios
            </Button>
          </section>
        )}

        <Field label="Titulo" htmlFor="anuncio-titulo">
          <Input
            id="anuncio-titulo"
            value={valores.title}
            maxLength={160}
            placeholder="Mantenimiento del sabado 27"
            onChange={(event) => set('title', event.target.value)}
          />
        </Field>

        <Field
          label="Mensaje"
          htmlFor="anuncio-cuerpo"
          hint="Los saltos de linea se respetan tal cual."
        >
          <Textarea
            id="anuncio-cuerpo"
            rows={7}
            value={valores.body}
            maxLength={8000}
            placeholder="Que pasa, a quien afecta y que tiene que hacer quien lo lea."
            onChange={(event) => set('body', event.target.value)}
          />
        </Field>

        <Field label="Clase" htmlFor="anuncio-clase">
          <Select
            id="anuncio-clase"
            value={valores.kind}
            options={OPCIONES_CLASE}
            onChange={(event) => set('kind', event.target.value as AnnouncementKind)}
          />
        </Field>

        <label className="flex items-center gap-2.5 text-[13px] text-text-secondary">
          <input
            type="checkbox"
            checked={valores.pinned}
            onChange={(event) => set('pinned', event.target.checked)}
            className="h-4 w-4 accent-[var(--color-primary)]"
          />
          Fijar arriba del canal
        </label>
      </div>
    </Modal>
  );
}
